import { useState } from 'react';
import {
  Bell,
  CheckCircle2,
  Clock,
  Filter,
  AlertTriangle,
  Info,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useComplianceAlerts,
  useResolveAlert,
} from '@/hooks/use-compliance-dashboard';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { AlertSeverity, AlertType, ComplianceAlert } from '@/lib/types';

// ─── Constants ──────────────────────────────────────────────────────────────

const SEVERITY_OPTIONS: { value: AlertSeverity | ''; label: string }[] = [
  { value: '', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const TYPE_OPTIONS: { value: AlertType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'cert_expiring', label: 'Cert Expiring' },
  { value: 'insurance_expiring', label: 'Insurance Expiring' },
  { value: 'audit_due', label: 'Audit Due' },
  { value: 'quarterly_stats_due', label: 'Stats Due' },
  { value: 'quarterly_stats_overdue', label: 'Stats Overdue' },
  { value: 'incident_deadline', label: 'Incident Deadline' },
  { value: 'document_review_due', label: 'Document Review' },
  { value: 'management_review_due', label: 'Management Review' },
  { value: 'evidence_expired', label: 'Evidence Expired' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'false', label: 'Active' },
  { value: 'true', label: 'Resolved' },
];

const SEVERITY_CONFIG: Record<AlertSeverity, { color: string; bg: string; icon: typeof AlertTriangle }> = {
  critical: { color: 'text-red-800', bg: 'bg-red-100', icon: XCircle },
  warning: { color: 'text-amber-800', bg: 'bg-amber-100', icon: AlertTriangle },
  info: { color: 'text-blue-800', bg: 'bg-blue-100', icon: Info },
};

// ─── Main Page ──────────────────────────────────────────────────────────────

export function AlertsPage() {
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | ''>('');
  const [typeFilter, setTypeFilter] = useState<AlertType | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: alerts, isLoading } = useComplianceAlerts({
    severity: severityFilter || undefined,
    alert_type: typeFilter || undefined,
    is_resolved: statusFilter || undefined,
  });

  const resolveMutation = useResolveAlert();

  const activeCount = alerts?.filter((a) => !a.is_resolved).length ?? 0;
  const resolvedCount = alerts?.filter((a) => a.is_resolved).length ?? 0;
  const criticalCount = alerts?.filter((a) => a.severity === 'critical' && !a.is_resolved).length ?? 0;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Compliance Alerts</h1>
        <p className="mt-1 text-sm text-gray-500">
          All compliance alerts — active and resolved
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className={criticalCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="flex items-center gap-3 pt-6">
            <XCircle className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold">{criticalCount}</p>
              <p className="text-sm text-gray-600">Critical</p>
            </div>
          </CardContent>
        </Card>
        <Card className={activeCount > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="flex items-center gap-3 pt-6">
            <Bell className="h-8 w-8 text-amber-600" />
            <div>
              <p className="text-2xl font-bold">{activeCount}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 pt-6">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold">{resolvedCount}</p>
              <p className="text-sm text-gray-600">Resolved</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as AlertSeverity | '')}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as AlertType | '')}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Alert list */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : !alerts || alerts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-gray-300" />
            <p className="mt-3 font-medium text-gray-600">No alerts found</p>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter || severityFilter || typeFilter
                ? 'Try adjusting your filters'
                : 'Your compliance alerts will appear here'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onResolve={() => resolveMutation.mutate(alert.id)}
              isResolving={resolveMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function AlertCard({
  alert,
  onResolve,
  isResolving,
}: {
  alert: ComplianceAlert;
  onResolve: () => void;
  isResolving: boolean;
}) {
  const timeZone = useOrgTimeZone();
  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = sev.icon;

  return (
    <Card className={alert.is_resolved ? 'opacity-60' : ''}>
      <CardContent className="flex items-start gap-4 pt-6">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${sev.color}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`${sev.bg} ${sev.color}`}>{alert.severity}</Badge>
            <Badge variant="outline" className="text-gray-600">
              {formatAlertType(alert.alert_type)}
            </Badge>
            {alert.is_resolved && (
              <Badge className="bg-green-100 text-green-800">Resolved</Badge>
            )}
          </div>
          <h3 className="mt-1.5 text-sm font-semibold text-gray-900">{alert.title}</h3>
          <p className="mt-0.5 text-sm text-gray-600">{alert.description}</p>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-400">
            <span>
              <Clock className="mr-1 inline h-3 w-3" />
              Created: {formatDateTimeInTimeZone(alert.created_at, timeZone, { dateStyle: 'medium' })}
            </span>
            {alert.due_date && (
              <span>
                Due: {formatDateOnly(alert.due_date)}
              </span>
            )}
            {alert.resolved_at && (
              <span>
                <CheckCircle2 className="mr-1 inline h-3 w-3 text-green-500" />
                Resolved: {formatDateTimeInTimeZone(alert.resolved_at, timeZone, { dateStyle: 'medium' })}
              </span>
            )}
          </div>
        </div>
        {!alert.is_resolved && (
          <Button
            variant="outline"
            size="sm"
            onClick={onResolve}
            disabled={isResolving}
            className="shrink-0"
          >
            Resolve
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Utilities ──────────────────────────────────────────────────────────────

function formatAlertType(t: string): string {
  const map: Record<string, string> = {
    cert_expiring: 'Cert Expiring',
    insurance_expiring: 'Insurance Expiring',
    audit_due: 'Audit Due',
    quarterly_stats_due: 'Stats Due',
    quarterly_stats_overdue: 'Stats Overdue',
    incident_deadline: 'Incident Deadline',
    document_review_due: 'Document Review',
    management_review_due: 'Management Review',
    evidence_expired: 'Evidence Expired',
    nominated_person_missing: 'Missing Appointment',
    custom: 'Custom',
  };
  return map[t] || t;
}
